import { CloseButton, TextInput } from "@mantine/core";
import { debounce } from "lodash-es";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "../SvgIconRaw";
import type { MeqTableLocalization } from "./types";

/**
 * Toolbar search input driving MeqTable's global filter (fuzzy match-sorter
 * parity — see filterFns.ts). Debounces commits to the table's
 * global-filter state by 250ms, matching the old MRT-based wrapper's
 * client-side default (its global-filter text field), so typing doesn't
 * re-filter on every keystroke.
 *
 * Localization: `search`/clear-button label default from i18next `table.*`
 * and are overridable via `localization` (same pattern as MeqTable.tsx's
 * other aria-label fallbacks).
 */

const DEBOUNCE_MS = 250;

export interface GlobalFilterInputProps {
  value: string;
  onChange: (value: string) => void;
  hasBorderedControls?: boolean;
  localization?: Partial<MeqTableLocalization>;
}

const GlobalFilterInput = ({
  value,
  onChange,
  hasBorderedControls = false,
  localization,
}: GlobalFilterInputProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(value);
  const placeholder = localization?.search ?? t("table.search");
  const clearSearchLabel = localization?.clearSearch ?? t("table.clearSearch");

  const debouncedOnChange = useMemo(
    () => debounce(onChange, DEBOUNCE_MS),
    [onChange],
  );

  const handleChange = (nextValue: string) => {
    setSearchValue(nextValue);
    debouncedOnChange(nextValue);
  };

  const handleClear = () => {
    setSearchValue("");
    debouncedOnChange.cancel();
    onChange("");
  };

  return (
    <TextInput
      size="sm"
      w={220}
      // Sit flush with the MeqTable card surface, defined by its border. Paper
      // defaults to --mantine-color-body, so that is the flush colour; a
      // bordered-controls table pins its card to bg-primary and puts `secondary`
      // buttons either side of this input, which pin the same token.
      styles={{
        input: {
          backgroundColor: hasBorderedControls
            ? "var(--mantine-color-bg-primary)"
            : "var(--mantine-color-body)",
          borderColor: "var(--mantine-color-border-secondary)",
        },
      }}
      placeholder={placeholder}
      aria-label={placeholder}
      value={searchValue}
      onChange={(event) => handleChange(event.currentTarget.value)}
      leftSection={<SvgIconRaw name="SearchSm" size={16} />}
      rightSection={
        searchValue ? (
          <CloseButton
            size="sm"
            aria-label={clearSearchLabel}
            onClick={handleClear}
          />
        ) : null
      }
    />
  );
};

export default GlobalFilterInput;
